import {
  SPOONACULAR_API_KEY,
  SPOONACULAR_RECIPE_INFO_API,
} from "../config";
import { readJSON, writeJSON } from "../utils/storage";
import { getRecipeId, toSummary } from "../utils/recipe";

export const MEALDB = "https://www.themealdb.com/api/json/v1/1";

/** Spoonacular request; the key comes from REACT_APP_SPOONACULAR_API_KEY (.env). */
export async function spoonFetchJSON(url) {
  const res = await fetch(`${url}${url.includes("?") ? "&" : "?"}apiKey=${SPOONACULAR_API_KEY}`);
  if (!res.ok) throw new Error(`Spoonacular ${res.status}`);
  return res.json();
}

async function mealdb(path) {
  const res = await fetch(`${MEALDB}/${path}`);
  if (!res.ok) throw new Error(`TheMealDB ${res.status}`);
  return res.json();
}

const toMealItem = (m) => ({
  id: m.idMeal,
  title: m.strMeal,
  image: m.strMealThumb,
  isMealDB: true,
  ...m,
});

/* ------------------------------------------------------ Recipe detail cache */
const CACHE_KEY = "recify_recipe_cache";
const CACHE_MAX = 30;

const cacheGet = (key) => (readJSON(CACHE_KEY, {})[key] || {}).data;

function cachePut(key, data) {
  const cache = readJSON(CACHE_KEY, {});
  cache[key] = { t: Date.now(), data };
  const keys = Object.keys(cache);
  if (keys.length > CACHE_MAX) {
    keys
      .sort((a, b) => cache[a].t - cache[b].t)
      .slice(0, keys.length - CACHE_MAX)
      .forEach((k) => delete cache[k]);
  }
  writeJSON(CACHE_KEY, cache);
}

/**
 * Full recipe details for a summary item ({ id, isMealDB }).
 * Falls back to the last copy we saw, so opened recipes still work offline.
 */
export async function fetchRecipeDetails(item) {
  const id = item.id ?? item.idMeal;
  const isMealDB = item.isMealDB ?? !!item.idMeal;
  const key = `${isMealDB ? "m" : "s"}:${id}`;
  try {
    let data;
    if (isMealDB) {
      const result = await mealdb(`lookup.php?i=${id}`);
      data = result.meals?.[0];
    } else {
      data = await spoonFetchJSON(`${SPOONACULAR_RECIPE_INFO_API}${id}/information`);
    }
    if (!data) throw new Error("Recipe not found");
    cachePut(key, data);
    return data;
  } catch (err) {
    const cached = cacheGet(key);
    if (cached) return cached;
    throw err;
  }
}

/* ----------------------------------------------------------------- Similar */
export async function fetchSimilar(recipe, excludedWords = []) {
  const id = getRecipeId(recipe);
  const ok = (title) =>
    !excludedWords.some((w) => String(title).toLowerCase().includes(w.toLowerCase()));

  if (recipe.extendedIngredients) {
    const list = await spoonFetchJSON(`${SPOONACULAR_RECIPE_INFO_API}${id}/similar?number=6`);
    return list
      .filter((r) => ok(r.title))
      .map((r) => ({
        id: String(r.id),
        title: r.title,
        image: `https://spoonacular.com/recipeImages/${r.id}-312x231.${r.imageType || "jpg"}`,
        isMealDB: false,
      }));
  }
  if (!recipe.strCategory) return [];
  const result = await mealdb(`filter.php?c=${encodeURIComponent(recipe.strCategory)}`);
  const pool = (result.meals || []).filter((m) => m.idMeal !== id && ok(m.strMeal));
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool.slice(0, 6).map((m) => toSummary(m));
}

/* ------------------------------------------------------ Ingredient discovery */
let ingredientListPromise = null;

export function fetchIngredientList() {
  if (!ingredientListPromise) {
    ingredientListPromise = mealdb("list.php?i=list")
      .then((r) => (r.meals || []).map((m) => m.strIngredient).filter(Boolean))
      .catch((e) => {
        ingredientListPromise = null;
        throw e;
      });
  }
  return ingredientListPromise;
}

/** Meals containing ALL the given ingredients (TheMealDB filters one at a time, so we intersect). */
export async function fetchByIngredients(ingredients) {
  const lists = await Promise.all(
    ingredients.map((name) =>
      mealdb(`filter.php?i=${encodeURIComponent(name.replace(/\s+/g, "_"))}`).then(
        (r) => r.meals || []
      )
    )
  );
  if (lists.length === 0) return [];
  const [first, ...rest] = lists;
  const idSets = rest.map((l) => new Set(l.map((m) => m.idMeal)));
  return first.filter((m) => idSets.every((s) => s.has(m.idMeal))).map(toMealItem);
}

export async function fetchSuggestions(query) {
  const result = await mealdb(`search.php?s=${encodeURIComponent(query)}`);
  return (result.meals || []).slice(0, 12).map((m) => ({
    id: m.idMeal,
    title: m.strMeal,
    image: m.strMealThumb,
  }));
}

export { toMealItem };
